<template>
  <div class="card">
    <input ref="fileInput" type="file" v-show="false" :accept="acceptTypes" @change="fileChange($event)" />
    <div class="card-header">
      <h3>{{ $t('importtask.settings_title') }} 
        <span v-if="advancedMode" class="text-danger"> ({{ $t('importtask.advancedMode') }})</span>
      </h3>
    </div>
    <div class="card-body">
      <form-container v-if="formControl" ref="formContainer" :error-message="formError" orientation="horizontal" >
        <div class="row">
          <div class="col">
            <form-wrapper v-if="formItems && formItems.Destination" :formConfig="formItems.Destination" @change="destinationChange" class="mb-1" />
            <div class="text-right form-group">
              <a @click="generateTemplate(null)" href="javascript:void(0)" class="p-0 border-0">{{ $t('importtask.generate_template') }}</a>
            </div>
            <div class="row">
              <div class="col-md-4">
                <label class="col-form-label form-group">{{ $t('importtask.labels.filename') }} </label>
              </div>
              <div class="col">
                <text-input :overrideInputCss="'col-md-12'" v-if="formItems && formItems.FileName" v-bind="formItems.FileName" v-model="formItems.FileName.value" />
              </div>
              <div class="col-auto pl-0">
                <loading-button :title="$t('importtask.select_file')" @click="openFileInput" />
              </div>
            </div>
            <form-wrapper :formConfig="formItems.Overwrite" />
          </div>
          <div class="col">
            <select-input v-bind="formItems.DecimalNotation" v-model="formItems.DecimalNotation.value" />
            <select-input v-bind="formItems.BooleanNotation" v-model="formItems.BooleanNotation.value" />
            <select-input v-bind="formItems.DateTimeNotation" v-model="formItems.DateTimeNotation.value" />
          </div>
        </div>
      </form-container>
    </div>
    <div class="card-footer text-right">
      <loading-button :enabled="fileUploaded" :title="$t('importtask.start_import')" @click="startImport" />
    </div>
    <specify-template-type v-if="openTypeModal && formItems" @close="openTypeModal = false" @generate="generateTemplate" :destination="formItems.Destination.value"/>
  </div>
</template>

<script>
import formmixin from '@/forms/components.js';
import { BooleanNotation, DateTimeNotation, DecimalNotation } from 'libs/enums.js';
import { FormBooleanConfig, FormControl, FormSelectConfig, FormTextConfig } from 'libs/classes/form.js';
import { decimalNotation, booleanNotation, dateTimeNotation } from '@/components/Export/notations';
import SpecifyTemplateType from './components/SpecifyTemplateType.vue';
import ImportController from '@/controller/importcontroller.js';
import fetchHelper from '@/libs/fetchcontroller';
import { geturi } from '@/libs/utils';
import ApiController from 'controller/apicontroller';
import { LocalStorage } from 'libs/clientstorage';

const controller = new ImportController();

export default {
  mixins: [formmixin],
  name: 'start-import',
  components: {
    'specify-template-type': SpecifyTemplateType
  },
  props: {
    fetchDestination: {
      default: ''
    }
  },
  data() {
    return {
      formError: null,
      formControl: null,
      formItems: null,
      advancedMode: false,
      fileUploaded: false,
      file: null,
      fileName: null,
      destination: null,
      openTypeModal: false,
      zipFileImportDestinations: [18,30,31,32,33],  // 18 is objectmiimport different filetype (zip) / 30 (Productimage) / 31 (ProductCategoryImage) / 32 (LocationImage) / 33 (ObjectImage)
      store: null
    };
  },
  async mounted(){
    this.advancedMode = await await this.$isSupportVerified();
    this.store = new LocalStorage(this.fetchDestination? `import-${this.fetchDestination}` : 'import-alldestinations');
    await this.FetchDestinations();
  },
  methods: {
    destinationChange(){
      let destinationValue = this.formItems.Destination.value;
      
      if(this.destination == null && this.zipFileImportDestinations.includes(destinationValue)){
        this.resetFile();
      }

      if(this.destination){
        if(!this.zipFileImportDestinations.includes(this.destination) && this.zipFileImportDestinations.includes(destinationValue)){
          this.resetFile();
        }
        if(this.zipFileImportDestinations.includes(this.destination) && !this.zipFileImportDestinations.includes(destinationValue)){
          this.resetFile();
        }
      }

      this.store.save('PreviousDestination', destinationValue);
      this.destination = destinationValue;
    },
    resetFile(){
      this.file = null;
      this.fileName = null;
      this.formItems.FileName.value = null;
      this.fileUploaded = false;
    },
    async FetchDestinations(){
      let filter = this.fetchDestination;

      let destinations = await controller.GetDestinations(filter);

      let formControl = this.loadForm();
      let defaultDestination = 0;

      if(this.fetchDestination !== ''){
        defaultDestination = destinations[0].value;
      }

      const savedDestination = this.store.get('PreviousDestination');
      if (savedDestination != null && savedDestination != undefined && destinations.some(d => d.value === savedDestination)){
        defaultDestination = savedDestination;
      }

      formControl.add(new FormSelectConfig('Destination', this.$t('importtask.choice'), defaultDestination , destinations, true));
      this.formControl = formControl;
      this.formItems = this.formControl.config;
    },
    loadForm(){
      const form = new FormControl();
      form.add(new FormTextConfig('FileName', this.$t('importtask.labels.filename'), null, false, false));
      form.add(new FormBooleanConfig('Overwrite', this.$t('importtask.labels.overwrite'), null));
      form.add(new FormSelectConfig('DecimalNotation', this.$t('exportdialog.decimalnotation.title'), DecimalNotation.Comma, decimalNotation, true));
      form.add(new FormSelectConfig('BooleanNotation', this.$t('exportdialog.booleannotation.title'), BooleanNotation.TrueFalse, booleanNotation, true));
      form.add(new FormSelectConfig('DateTimeNotation', this.$t('exportdialog.datetimenotation.title'), DateTimeNotation.DMYYYY, dateTimeNotation, true));

      form.config['FileName'].overrideLabelCss = { 'd-none': true };

      return form;
    },
    openFileInput(){
      this.$refs.fileInput.click();
    },
    async fileChange(event) {
      this.file = event.target.files[0];
      
      this.fileUploaded = true;

      let fileName = this.file.name;
      this.formItems.FileName.value = fileName;
    },
    async startImport(){
      const self = this;
      let formdata = new FormData();
      formdata.append('Destination', this.formItems.Destination.value);
      formdata.append('File', this.file);
      formdata.append('Overwrite', this.formItems.Overwrite.value);
      formdata.append('BooleanFormat', this.formItems.BooleanNotation.value);
      formdata.append('DecimalFormat', this.formItems.DecimalNotation.value);
      formdata.append('DateFormat', this.formItems.DateTimeNotation.value);

      const loader = this.$dialogs.loader('');
      try {
        this.formError = null;
        let result = await fetchHelper.postFile(geturi('mvc/import/StartImport'), formdata);
        this.$emit('importCreated', result);
      }
      catch(error){
        error.then(result => {
          if(result.Error){
            self.formError = result.Error;
          }
        });
      }
      finally {
        loader();
      }
    },
    async generateTemplate(chosenTypeId = null){
      let destination = this.formItems.Destination.value;
      let data = {
        Destination: destination,
        ObjectTypeID: null, 
        LocationTypeID: null
      };

      if([10,13].includes(destination)){
        if(chosenTypeId == null && !this.openTypeModal){
          this.openTypeModal = true;
          return;
        }
        else {
          if(destination === 13){
            data.ObjectTypeID = chosenTypeId;
          }
          if(destination === 10){
            data.LocationTypeID = chosenTypeId;
          }
        }
      }

      this.openTypeModal = false;
      const loader = this.$dialogs.loader();

      try {
        let response = await this.downloadTemplate(data);
        const contentDisposition = response.headers.get('content-disposition');
        // Can only be resolved in production
        let filename = contentDisposition ? contentDisposition.split('filename=')[1] : 'import_template.xlsx'; 

        filename = filename.replace(/['"]+/g, '');

        const url = window.URL.createObjectURL(await response.blob());
        const link = document.createElement('a');

        link.href = url;
        link.download = filename.trim();
        document.body.appendChild(link);
        link.click();

        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } 
      catch(error){
        if(error.Error){
          this.$toaster.showError(error.Error);
        }
      }
      finally {
        loader();
      }
    },
    async downloadTemplate(data){
      let uri = `${ApiController.BaseURI}mvc/import/GenerateTemplate`;

      const response = await fetch(uri, {
        method: 'post',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json; charset=utf-8'
        },
        body: JSON.stringify(data)
      });

      if (response.ok) return response;
      else {
        const error = await response.json();
        return Promise.reject(error);
      }
    }
  },
  computed: {
    acceptTypes(){
      if(this.formItems && this.formItems.Destination && this.zipFileImportDestinations.includes(this.formItems.Destination.value)){
        return '.zip, application/zip';
      }
      return '.xls, .xlsx';
    }
  }
};
</script>